'use client';

import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button, designTokens } from '@webapp/ui';
import { useI18n } from './i18n';

type ConsentChoice = 'accepted-optional' | 'rejected-optional';

const storageKey = 'webapp.cookieConsent.v1';

export function CookieConsent() {
  const { t } = useI18n();
  const [choice, setChoice] = useState<ConsentChoice | null>('rejected-optional');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const stored = window.localStorage.getItem(storageKey);
    setChoice(stored === 'accepted-optional' || stored === 'rejected-optional' ? stored : null);
    setReady(true);
  }, []);

  function save(nextChoice: ConsentChoice) {
    window.localStorage.setItem(storageKey, nextChoice);
    setChoice(nextChoice);
  }

  if (!ready || choice !== null) return null;

  // A slim full-width bar: tall floating cards covered the landing page's feature cards
  // and the login form until dismissed. Everything wraps inside the viewport — the
  // actions must never spill past the screen edge on small phones.
  return (
    <aside
      aria-label={t('cookies.label')}
      style={{
        background: designTokens.colors.surfaceContainerLow,
        borderTop: `1px solid ${designTokens.colors.outlineVariant}`,
        bottom: 0,
        boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.18)',
        boxSizing: 'border-box',
        color: designTokens.colors.onSurface,
        left: 0,
        padding: '10px 16px',
        position: 'fixed',
        right: 0,
        zIndex: 100,
      }}
    >
      <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', margin: '0 auto', maxWidth: 960 }}>
        <p style={{ color: designTokens.colors.onSurfaceVariant, flex: '1 1 280px', fontSize: 13, lineHeight: 1.45, margin: 0, minWidth: 0 }}>
          <strong style={{ color: designTokens.colors.onSurface }}>{t('cookies.title')}</strong> · {t('cookies.body')}
        </p>
        <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', minWidth: 0 }}>
          <Link
            to="/privacy"
            style={{ color: designTokens.colors.onSurfaceVariant, fontSize: 13, fontWeight: 650, textDecoration: 'none', whiteSpace: 'nowrap' }}
          >
            {t('cookies.privacy')}
          </Link>
          <Button type="button" onClick={() => save('rejected-optional')} variant="secondary" style={{ fontSize: 13, padding: '6px 12px' }}>
            {t('cookies.reject')}
          </Button>
          <Button type="button" onClick={() => save('accepted-optional')} style={{ fontSize: 13, padding: '6px 12px' }}>
            {t('cookies.accept')}
          </Button>
        </div>
      </div>
    </aside>
  );
}
